// `/warmup` (UI-6, simplified 2026-10-23 on the owner's word: "600 settings",
// "it will all blur together"). On top, the three SES accounts' daily numbers —
// the only primary controls — each saying why when its number reaches nobody;
// under them, the twelve countries with one switch each ("Klaviyo is küld").
// Everything else — the pools with their per-IP figures, N, the waves, the
// handover's other moves, the decisions and logs — is in "Részletek (haladó)",
// closed until opened. The page asks again once a minute.

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FlameIcon } from "lucide-react";
import { PageHeader } from "@/components/emc/PageHeader";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { useHaltStatus } from "@/pages/campaigns/data";
import { HaltButton } from "@/pages/warmup/actions";
import { HandoverPanel } from "@/pages/warmup/HandoverPanel";
import { useContactSummary, useHandoverRows, useWarmupBoard } from "@/pages/warmup/data";
import { dayStartText, timeAtViewer } from "@/components/emc/time";
import { AccountCard, AccountHistory } from "@/pages/warmup/AccountCard";
import { CountryList } from "@/pages/warmup/CountryList";
import { PoolCard } from "@/pages/warmup/PoolCard";
import { SWITCH, countryRows, poolZeroReason } from "@/pages/warmup/simple";

/** A link into the page that points inside "Részletek" opens it. */
function inDetails(hash: string): boolean {
  return /^#(pool-|handover|details)/.test(hash);
}

export default function WarmupBoard() {
  const board = useWarmupBoard();
  const handover = useHandoverRows();
  const summary = useContactSummary();
  const halt = useHaltStatus();
  const data = board.data;
  const { hash } = useLocation();
  const loaded = data !== null;
  const [details, setDetails] = useState(() => inDetails(hash));

  // The overview links to a pool's card (`/warmup#pool-HU`); the router does
  // not scroll to a fragment, so the page does, once the cards are there —
  // opening "Részletek" first when the card is inside it.
  useEffect(() => {
    if (!loaded || !hash) return;
    if (inDetails(hash)) setDetails(true);
    window.setTimeout(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
    }, 0);
  }, [loaded, hash]);

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const pools = useMemo(() => data?.pools ?? [], [data]);
  const rows = useMemo(
    () => countryRows({ handover: handover.data, accounts, pools, summary: summary.data }),
    [handover.data, accounts, pools, summary.data],
  );
  const zone = pools.find((pool) => pool.code === "HU")?.timezone ?? pools[0]?.timezone;

  function reload() {
    void board.reload();
    void handover.reload();
  }

  return (
    <>
      <PageHeader
        title="Warmup"
        description={
          <>
            SES-fiókonként egy napi szám: a kód a fiók országai közt osztja szét, a küldhető kontaktok
            arányában. Egy ország a {SWITCH} kapcsolóval nyílik meg. Minden más — poolok, N, hullámok,
            naplók — lent, a Részletekben.
          </>
        }
        actions={<HaltButton halted={halt.status?.halted ?? false} />}
      />

      <div className="flex flex-col gap-4">
        {halt.status?.halted && (
          <Callout tone="warn">
            A teljes kiküldés áll (a piros sáv fent): amíg a leállítás él, egyik pool sem küld. A
            poolok saját állapota nem változott — a feloldás után onnan folytatják.
          </Callout>
        )}
        {board.error !== null && <ErrorState message={board.error} onRetry={() => void board.reload()} />}
        {data === null && board.loading && <LoadingState label="A warmup betöltése…" />}
        {data !== null && pools.length === 0 && (
          <EmptyState icon={FlameIcon} title="Nincs egyetlen IP-pool sem felvéve." />
        )}
        {data !== null && pools.length > 0 && (
          <>
            {accounts.length > 0 && (
              <div className="grid items-start gap-4 xl:grid-cols-3">
                {accounts.map((account) => (
                  <AccountCard key={account.code} account={account} countries={rows} onChanged={reload} />
                ))}
              </div>
            )}

            <CountryList
              rows={rows}
              loading={handover.data === null && handover.error === null}
              error={handover.error}
              onChanged={reload}
            />

            <details
              id="details"
              open={details}
              onToggle={(event) => setDetails(event.currentTarget.open)}
              className="scroll-mt-6 rounded-card bg-card shadow-card"
            >
              <summary className="cursor-pointer px-5.5 py-4 text-body font-semibold">
                Részletek (haladó)
                <span className="ml-2 font-normal text-muted-foreground">
                  poolok IP-nkénti számokkal, N és hullámok, átállás, döntések és naplók
                </span>
              </summary>
              <div className="flex flex-col gap-4 border-t border-separator p-4">
                <p className="text-label text-muted-foreground">
                  Frissítve {board.at ? timeAtViewer(board.at) : "—"} · percenként frissül · napok,
                  ahogy az ütemező és a limiter számol: {dayStartText(data.today, zone)}; az időpontok
                  a te órád szerint.
                </p>
                {accounts.some((account) => account.decision || account.events.length > 0) && (
                  <div className="grid items-start gap-4 rounded-card border border-separator p-4 xl:grid-cols-3">
                    {accounts.map((account) => (
                      <AccountHistory key={account.code} account={account} />
                    ))}
                  </div>
                )}
                <div className="grid items-start gap-4 xl:grid-cols-2">
                  {pools.map((pool) => (
                    <PoolCard
                      key={pool.code}
                      pool={pool}
                      knobs={data.knobs}
                      thresholds={data.thresholds}
                      why={poolZeroReason(
                        pool,
                        accounts.find((account) => account.code === pool.warmup_account) ?? null,
                        rows,
                      )}
                      onChanged={reload}
                    />
                  ))}
                </div>
                <HandoverPanel rows={handover.data} error={handover.error} reload={reload} />
              </div>
            </details>
          </>
        )}
      </div>
    </>
  );
}
